import { create } from "zustand";
import { devtools } from "zustand/middleware";

import { CoachingArtifact } from "../hooks/useCoachingArtifacts";
import { createArtifactsActions } from "./actions/artifactsActions";
import { createAttributeSelectionActions } from "./actions/attributeSelectionActions";
import { createConfigurationActions } from "./actions/configurationActions";
import { createInsightsActions } from "./actions/insightsActions";

export interface CoachingGeneratorState {
  customPrompt: string;
  systemPrompt: string;
  temperature: number;
  includeAttributeStats: boolean;
  includeLensDefinition: boolean;
  includeTeamBestPractices: boolean;
  selectedAttributes: Set<string>;
  expandedAttribute: string | null;
  isGeneratingInsights: boolean;
  error: string;
  existingArtifacts: CoachingArtifact[];
}

export interface ConfigurationActions {
  setCustomPrompt: (prompt: string) => void;
  setSystemPrompt: (prompt: string) => void;
  setTemperature: (temperature: number) => void;
  setIncludeAttributeStats: (include: boolean) => void;
  setIncludeLensDefinition: (include: boolean) => void;
  setIncludeTeamBestPractices: (include: boolean) => void;
}

export interface AttributeSelectionActions {
  toggleAttribute: (attribute: string) => void;
  selectAllAttributes: (attributes: string[]) => void;
  clearSelectedAttributes: () => void;
  setExpandedAttribute: (attribute: string | null) => void;
}

export interface GenerateInsightsParams {
  attributeExamples: any[];
  attributeStatsContent: string | null;
  lensDefinitionContent: string | null;
  teamBestPracticesContent: string | null;
  selectedUserEmail: string;
  lensName: string;
  timeRange: string;
  timezone: string;
  tenantId: string | null;
  coachingLensPrompt?: string;
  coachingLensTitle?: string;
  coachingLensModel?: string;
  coachingLensTemperature?: number;
  coachingLensMaxTokens?: number;
}

export interface InsightsActions {
  setIsGeneratingInsights: (isGenerating: boolean) => void;
  setError: (error: string) => void;
  clearError: () => void;
  generateInsights: (params: GenerateInsightsParams) => Promise<string | null>;
}

export interface ArtifactsActions {
  setExistingArtifacts: (artifacts: CoachingArtifact[]) => void;
}

export interface CoachingGeneratorActions
  extends ConfigurationActions,
  AttributeSelectionActions,
  InsightsActions,
  ArtifactsActions {}

type CoachingGeneratorStore = CoachingGeneratorState & CoachingGeneratorActions;

export const useCoachingGeneratorStore = create<CoachingGeneratorStore>()(
  devtools(
    (set, get) => ({
      customPrompt: "",
      systemPrompt: "",
      temperature: 0.1,
      includeAttributeStats: true,
      includeLensDefinition: true,
      includeTeamBestPractices: true,
      selectedAttributes: new Set<string>(),
      expandedAttribute: null,
      isGeneratingInsights: false,
      error: "",
      existingArtifacts: [],

      ...createConfigurationActions(set),
      ...createAttributeSelectionActions(set),
      ...createInsightsActions(set, get),
      ...createArtifactsActions(set)
    }),
    {
      name: "coaching-generator-store"
    }
  )
);
